// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import moment from 'moment-timezone';
import { formatValue } from './deliverables';

interface IPromotionWindow {
  startDate: string;
  endDate: string;
  startTimezone: string;
  endTimezone: string;
}

export type PromotionStatus = 'Inactive' | 'Upcoming' | 'Active' | 'Expired';

interface IPromotionLifecycle {
  active: boolean;
  startDate: string;
  endDate: string;
}

const round2 = (value: number): number => +value.toFixed(2);

// Each bound formats in the zone it was expressed in (a 12:00 AM Eastern start, an 11:59 PM Hawaii end):
// formatting the start in Hawaii time, or the end in UTC, would print the wrong day.
export const formatPromotionWindow = (promotion: IPromotionWindow, format = 'MMMM D'): string => (
  `${moment.tz(promotion.startDate, promotion.startTimezone).format(format)} - ${moment.tz(promotion.endDate, promotion.endTimezone).format(format)}`
);

export const formatOfferEnds = (endDate: string, endTimezone: string): string => (
  `Offer ends ${moment.tz(endDate, endTimezone).format('MMMM D, YYYY')}`
);

// One bound in the zone it was expressed in, abbreviation included: "Sep 8, 2026 12:00 AM EDT"
export const formatPromotionBound = (date: string, timezone: string): string => (
  moment.tz(date, timezone).format('MMM D, YYYY h:mm A z')
);

// The stored instant, for anyone who needs to match it against Mongo
export const formatUtcInstant = (date: string): string => moment.utc(date).format('YYYY-MM-DD HH:mm:ss.SSS [UTC]');

// Inactive wins over the window; the window is inclusive at both ends, as the server's resolver treats it
export const promotionStatus = (promotion: IPromotionLifecycle, now: Date = new Date()): PromotionStatus => {
  if (!promotion.active) return 'Inactive';
  const at = now.getTime();
  if (at < new Date(promotion.startDate).getTime()) return 'Upcoming';
  if (at > new Date(promotion.endDate).getTime()) return 'Expired';
  return 'Active';
};

// Mirrors aw-server promotion_util.computePromotionDollars so the checkout summary matches the order the server writes
export const promotionDollarsOff = (credits: number, baseRate: number, valuePercent: number): number => {
  const baseAmount = round2(credits * baseRate);
  const net = round2(credits * (baseRate * (1 - valuePercent / 100)));
  return round2(baseAmount - net);
};

export const formatDiscountAmount = (amount: number): string => `-$${formatValue(amount)}`;

// The order-history value for a promotion: the dollars off when known and showable, else the percent (legacy / redacted)
export const formatOrderPromotionValue = (promotion: IOrderPromotion, hideDollars: boolean): string => (
  !hideDollars && typeof promotion.amount === 'number' ? formatDiscountAmount(promotion.amount) : `${promotion.valuePercent}% off`
);
